import { FC } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useMenu from 'app/hooks/useMenu';
// Redux
import { useAppDispatch, useAppSelector } from 'app/hooks/useStore';
// Actions
import { DMSBatchesActions } from 'app/store/DMSBatches/DMSBatches.slice';
// Selectors
import { selectPreferences, selectSort } from 'app/store/DMSBatches/DMSBatches.selectors';
// MUI
import { Box, Divider, Tooltip } from '@mui/material';
// Icons
import {
  SettingsOutlined as SettingsOutlinedIcon,
  Check as CheckIcon
} from '@mui/icons-material';
// Components
import { IconButton } from 'app/components/Mui';
import Icon from 'app/components/Icon';

const DocumentsPreference:FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const sort = useAppSelector(selectSort);
  const preferences = useAppSelector(selectPreferences);

  const setSort = (sortBy:string, direction:'asc' | 'desc') => {
    dispatch(DMSBatchesActions.setSort({ sortBy, direction }));
  }

  const togglePreference = (field:keyof typeof preferences) => () => {
    dispatch(DMSBatchesActions.setPreference({ field, value: !preferences[field] }));
  }

  const { Menu:SortMenu, MenuItem:SortMenuItem, openMenu:openSortMenu } = useMenu();
  const { Menu, MenuItem, openMenu } = useMenu();

  const createMenuOption =
  (MenuItemComponent: typeof MenuItem) =>
  ({ name, checked, onClick, children }: {
    name: string;
    checked: boolean;
    onClick: () => void;
    children: React.ReactNode;
  }) => (
    <MenuItemComponent
      name={name}
      onClick={onClick}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 1 }}
    >
      {checked ? <CheckIcon sx={{ fontSize: 16 }} /> : <Box sx={{ width: 16 }} />}
      <span>{children}</span>
    </MenuItemComponent>
  );

  const SortMenuOption = createMenuOption(SortMenuItem);
  const PreferenceMenuOption = createMenuOption(MenuItem);

  return (
    <Box sx={{
      pt: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 1
    }}>
      <Tooltip title={t('labels.sort')}>
        <div>
          <IconButton
            name="documents-sort-button"
            onClick={openSortMenu}
            size="small"
          >
            <Icon
              icon="sort-amount-desc"
              rootSx={{ p: 0.5 }}
              sx={{ width: '20px', height: '20px', fontSize: '20px', transform: 'rotateX(180deg)' }}
            />
          </IconButton>
        </div>
      </Tooltip>
      <SortMenu>
        <SortMenuOption
          name="sort-documents-by-created-date-desc"
          checked={sort.sortBy === 'createdOn' && sort.direction === 'desc'}
          onClick={() => setSort('createdOn', 'desc')}
        >{t('components.filesSidebar.sort.createdDateNewToOld')}</SortMenuOption>
        <SortMenuOption
          name="sort-documents-by-created-date-asc"
          checked={sort.sortBy === 'createdOn' && sort.direction === 'asc'}
          onClick={() => setSort('createdOn', 'asc')}
        >{t('components.filesSidebar.sort.createdDateOldToNew')}</SortMenuOption>
        {!preferences.groupByBatches ? [
          <Divider key="divider-1" />,
          <SortMenuOption
            key="sort-documents-by-modified-date-desc"
            name="sort-documents-by-modified-date-desc"
            checked={sort.sortBy === 'modifiedOn' && sort.direction === 'desc'}
            onClick={() => setSort('modifiedOn', 'desc')}
          >{t('components.filesSidebar.sort.modifiedDateNewToOld')}</SortMenuOption>,
          <SortMenuOption
            key="sort-documents-by-modified-date-asc"
            name="sort-documents-by-modified-date-asc"
            checked={sort.sortBy === 'modifiedOn' && sort.direction === 'asc'}
            onClick={() => setSort('modifiedOn', 'asc')}
          >{t('components.filesSidebar.sort.modifiedDateOldToNew')}</SortMenuOption>,
          <Divider key="divider-2" />,
          <SortMenuOption
            key="sort-documents-by-name-asc"
            name="sort-documents-by-name-asc"
            checked={sort.sortBy === 'name' && sort.direction === 'asc'}
            onClick={() => setSort('name', 'asc')}
          >{t('components.filesSidebar.sort.documentNameAtoZ')}</SortMenuOption>,
          <SortMenuOption
            key="sort-documents-by-name-desc"
            name="sort-documents-by-name-desc"
            checked={sort.sortBy === 'name' && sort.direction === 'desc'}
            onClick={() => setSort('name', 'desc')}
          >{t('components.filesSidebar.sort.documentNameZtoA')}</SortMenuOption>,
        ] : null}
      </SortMenu>
      <Tooltip title={t('labels.settings')}>
        <div>
          <IconButton
            name="documents-settings-button"
            onClick={openMenu}
            size="small"
          >
            <SettingsOutlinedIcon />
          </IconButton>
        </div>
      </Tooltip>
      <Menu>
        <PreferenceMenuOption
          name="hide-deleted-documents"
          checked={!preferences.hideDeletedDocuments}
          onClick={togglePreference('hideDeletedDocuments')}
        >{t('buttons.showDeletedDocuments')}</PreferenceMenuOption>
        <PreferenceMenuOption
          name="group-by-batches"
          checked={preferences.groupByBatches}
          onClick={togglePreference('groupByBatches')}
        >{t('buttons.groupByBatches')}</PreferenceMenuOption>
      </Menu>
    </Box>
  );
}

export default DocumentsPreference;
